import { useEffect, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import FormInput from "../../Form/FormInput";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import {
  addLocalNotification,
  clearInventoryWriteDown,
  requestInventoryWriteDown,
  useAuthState,
  useBusinessProductDispatch,
  useBusinessProductState,
  useNotificationDispatch,
} from "../../../../Context";
import { currencyFormat } from "../../../../Utils";

// PSAK 14 lower-of-cost-or-NRV write-down. Lets the user drop a product's
// moving-average cost to a net realisable value below it; the backend posts the
// expense journal and records the WRITE_DOWN inventory-ledger entry.
const ProductWriteDownModal = ({ open, onClose, product }) => {
  const { t } = useTranslation();

  const [nrv, setNrv] = useState("");
  const [fieldError, setFieldError] = useState(null);

  const dispatch = useBusinessProductDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  const { writeDownLoading, writeDownError, writeDownResult } =
    useBusinessProductState();

  // Current moving-average cost (fall back to the static/last cost).
  const averageCost =
    product?.averageCost != null ? product.averageCost : product?.cost ?? 0;
  const quantityOnHand = product?.currentStock ?? 0;

  // Clear any residual write-down state on unmount.
  useEffect(() => {
    return () => {
      clearInventoryWriteDown(dispatch);
    };
  }, [dispatch]);

  // React to completion (real write-down or a server-side no-op).
  useEffect(() => {
    if (
      product?.id &&
      writeDownResult &&
      writeDownResult.productID === product.id
    ) {
      const applied = Number(writeDownResult.writedownAmount) > 0;
      clearInventoryWriteDown(dispatch);
      onClose();
      addLocalNotification(notificationDispatch, {
        id: `writedown-${product.id}`,
        type: applied ? "success" : "info",
        title: applied ? "common.success" : "common.info",
        body: applied
          ? "products.notifications.writeDownSuccess"
          : "products.notifications.writeDownNoop",
        showFull: true,
      });
    }
  }, [product?.id, writeDownResult, dispatch, notificationDispatch, onClose]);

  const validate = () => {
    if (quantityOnHand <= 0) {
      setFieldError("products.errors.noStockToWriteDown");
      return false;
    }
    if (nrv === "" || nrv === null) {
      setFieldError("products.errors.nrvRequired");
      return false;
    }
    if (isNaN(Number(nrv))) {
      setFieldError("products.errors.nrvNumber");
      return false;
    }
    if (Number(nrv) < 0) {
      setFieldError("products.errors.nrvNegative");
      return false;
    }
    if (Number(nrv) >= averageCost) {
      setFieldError("products.errors.nrvBelowCost");
      return false;
    }
    setFieldError(null);
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (writeBlocked) return;
    if (!validate()) return;
    requestInventoryWriteDown(dispatch, {
      productId: product.id,
      nrv: Number(nrv),
    });
  };

  const estimatedImpact =
    nrv !== "" && !isNaN(Number(nrv)) && Number(nrv) < averageCost
      ? (averageCost - Number(nrv)) * quantityOnHand
      : 0;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="products.modal.title.writeDown"
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={writeBlocked}
            loading={writeDownLoading}
            type="submit"
            form="writedown-form"
            variant="primary"
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <form
        id="writedown-form"
        className="space-y-4 mb-6"
        onSubmit={handleSubmit}
      >
        <WriteBlockGate>
        <FormError error={writeDownError} />
        <Fieldset
          disabled={writeDownLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          {/* Product summary */}
          <div className="bg-gray-50 p-4 rounded-md space-y-2">
            <div>
              <h3 className="text-sm font-medium text-gray-500">
                {t("products.label_one")}
              </h3>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {product?.name}
              </p>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {t("products.fields.averageCost")}
              </span>
              <span className="text-gray-900">
                {currencyFormat(averageCost)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">
                {t("products.modal.stock.label")}
              </span>
              <span className="text-gray-900">{quantityOnHand}</span>
            </div>
          </div>

          {/* NRV input */}
          <Field>
            <FormInput
              required
              label="products.fields.nrv"
              hint={t("products.hints.nrv")}
              id="nrv-value"
              name="nrv_value"
              type="number"
              min="0"
              step="any"
              prefix="Rp"
              value={nrv}
              onChange={(e) => {
                setNrv(e.target.value);
                setFieldError(null);
              }}
              placeholder={t("products.placeholders.nrv")}
              error={fieldError}
            />
          </Field>

          {/* Impact preview */}
          {estimatedImpact > 0 && (
            <div className="bg-blue-50 p-3 rounded-md">
              <div className="text-sm text-blue-700">
                {t("products.modal.writeDown.impactLabel")}{" "}
                <span className="font-semibold">
                  <NumericFormat
                    displayType="text"
                    value={estimatedImpact}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    prefix="Rp"
                  />
                </span>
              </div>
            </div>
          )}
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default ProductWriteDownModal;
